import { SensorDisplayValue } from '../value-objects/SensorDisplayValue';
import {
  VISUALIZATION_SENSOR_DISPLAY_CONFIG,
  type RegisterDisplayItem,
} from '@/features/panels/constants/panelDetailTab.constants';

// ========== 型定義 ==========

/** APIから返される見える化データの生データ型 */
export interface VisualizationApiData {
  readonly panelId: number;
  readonly receivedAt: string | null;
  readonly visualization: VisualizationApiSensorValues | null;
}

/** APIレスポンスのセンサー値オブジェクト */
interface VisualizationApiSensorValues {
  readonly [fieldName: string]: number | null;
}

interface PanelVisualizationConstructorParams {
  readonly panelId: number;
  readonly receivedAt: Date | null;
  readonly sensorValues: readonly SensorDisplayValue[];
}

// ========== エンティティ ==========

/**
 * 見える化データエンティティ
 *
 * APIレスポンスからファクトリメソッドで生成し、
 * 10個のSensorDisplayValue値オブジェクトを保持する。
 */
export class PanelVisualization {
  private readonly panelId: number;
  private readonly receivedAt: Date | null;
  private readonly sensorValues: readonly SensorDisplayValue[];

  private constructor(params: PanelVisualizationConstructorParams) {
    this.panelId = params.panelId;
    this.receivedAt = params.receivedAt;
    this.sensorValues = params.sensorValues;
  }

  /**
   * APIレスポンスからエンティティを生成
   *
   * VISUALIZATION_SENSOR_DISPLAY_CONFIG を参照し、
   * 各フィールドに対応するSensorDisplayValueを生成する。
   */
  public static createFromApiResponse(apiData: VisualizationApiData): PanelVisualization {
    const visualizationValues = apiData.visualization;

    const sensorValues = VISUALIZATION_SENSOR_DISPLAY_CONFIG.map(
      (config) => SensorDisplayValue.create({
        displayName: config.displayName,
        rawValue: visualizationValues !== null
          ? (visualizationValues[config.fieldName] ?? null)
          : null,
        unit: config.unit,
        precision: config.precision,
        divisor: config.divisor,
      })
    );

    return new PanelVisualization({
      panelId: apiData.panelId,
      receivedAt: apiData.receivedAt !== null ? new Date(apiData.receivedAt) : null,
      sensorValues,
    });
  }

  public getPanelId(): number {
    return this.panelId;
  }

  public getReceivedAt(): Date | null {
    return this.receivedAt;
  }

  /**
   * プレゼンテーションモデルに変換
   */
  public toPresentationModel(): readonly RegisterDisplayItem[] {
    return this.sensorValues.map((sensorValue) => sensorValue.toPresentationModel());
  }
}
